import React from 'react';
import { DataGrid } from '@mui/x-data-grid';
import { ActionContext } from '../../../context/ActionContext';
import { CmicContext } from '../../../context/CmicContext';
import { PAGE_LIMIT } from '../../../shared/Constants';
import { COLORS } from '../../../utility/tasks/colors';
import { dataGridStyling, dataGridsx } from '../../../shared/styles';
import CustomPagination from '../../../components/common/CustomPagination';
import CustomDataGridColumnMenu from '../../../components/common/CustomDataGridColumnMenu';
import { isBGClrDark } from '../../../utility/UtilityFunctions';
import { filterFormatter } from '../../../utility/grid/helper/FliterHelper';
import { handleSortFormat } from '../../../utility/grid/helper/SortHelper';
import { isInternetAvailable } from '../../../shared/UtilityFunctions';

const CustomDatagrid = ({ rows, columns, currentOfficeTheme, ...otherProps }) => {
	const { nodeData, pageInfo, activeNode, sortModel, isLoading, setSortModel, fetchDataPagination, handleDataFetch } =
		React.useContext(ActionContext);
	const { setShowToast } = React.useContext(CmicContext);

	// Used to avoid multiple clicks
	const isNodeClicked = React.useRef(false);

	// Disable multiple or unwanted clicks while fetching data
	const disableClicks = () => {
		isNodeClicked.current = setTimeout(() => {
			clearTimeout(isNodeClicked.current);
			isNodeClicked.current = null;
		}, 500);
	};

	// @desc handles row click
	const handleNodeClick = (node, index) => {
		if (!isInternetAvailable(setShowToast)) {
			return;
		}
		if (!node || isNodeClicked.current || isLoading) return;
		disableClicks();
		handleDataFetch(node, nodeData?.items[index]);
	};

	//@desc Implemented to handle sorting, we can have only one sort at one time
	const handleSortChange = React.useCallback(
		data => {
			if (!isInternetAvailable(setShowToast)) {
				return;
			}
			if (isNodeClicked.current || isLoading) return;

			disableClicks();

			const orderBy = handleSortFormat(data, activeNode);
			fetchDataPagination(activeNode, { num: 0, orderBy });
			// Handling sort model to store value for future reference
			setSortModel(prev => ({
				...prev,
				[activeNode]: data.length > 0 ? [{ field: data[0]?.field || '', sort: data[0]?.sort || '' }] : [],
			}));
		},
		[isNodeClicked.current, isLoading, activeNode],
	);

	//@desc Implemented to handle filteration, we can only have one filter at one time and that too for specified columns
	const handleFilterationChange = React.useCallback(
		(data, e) => {
			if (isNodeClicked.current || isLoading) return;

			disableClicks();

			const filter = !data || data.items.length === 0 ? '' : filterFormatter(data?.items[0]);
			fetchDataPagination(activeNode, { num: 0, filter });
		},
		[isNodeClicked.current, isLoading, activeNode],
	);

	//@desc Implemented to handle pagination, previous data will be replaced with new data on page change
	const handlePaginationModelChange = React.useCallback(
		(num = 0) => {
			if (!isInternetAvailable(setShowToast)) {
				return;
			}
			if (isNodeClicked.current || isLoading) return;

			disableClicks();

			fetchDataPagination(activeNode, { num });
		},
		[isNodeClicked.current, isLoading, activeNode],
	);

	return (
		<DataGrid
			loading={isLoading}
			disableSelectionOnClick={isLoading}
			disableColumnSelector
			disableColumnFilter
			pagination
			rows={rows}
			columns={columns}
			rowHeight={30}
			headerHeight={30}
			rowsPerPageOptions={[PAGE_LIMIT]}
			paginationMode="server"
			sortingMode="server"
			filterMode="server"
			aria-label="Grid"
			rowCount={nodeData?.totalResults || 0}
			pageSizeOptions={[PAGE_LIMIT]}
			sortModel={sortModel[activeNode] || []}
			sortingOrder={['asc', 'desc', null]}
			localeText={{ noRowsLabel: 'No records found' }}
			slots={{
				columnMenu: props => CustomDataGridColumnMenu(currentOfficeTheme, props),
				pagination: props =>
					CustomPagination(
						nodeData?.totalResults || 0,
						handlePaginationModelChange,
						currentOfficeTheme,
						props,
					),
			}}
			paginationModel={{
				page: pageInfo[activeNode]?.num || 0,
				pageSize: PAGE_LIMIT,
			}}
			initialState={{
				pagination: {
					paginationModel: {
						pageSize: PAGE_LIMIT,
					},
				},
			}}
			onFilterModelChange={handleFilterationChange}
			onRowClick={params => {
				handleNodeClick(params?.row?.ecmParentNodeType || params?.row?.Type || '', params?.row?.index || 0);
			}}
			onSortModelChange={data => handleSortChange(data)}
			style={{
				minHeight: '25vh',
				maxHeight: '100%',
			}}
			sx={dataGridStyling(
				currentOfficeTheme.bodyForegroundColor,
				currentOfficeTheme.bodyBackgroundColor,
				isBGClrDark() ? currentOfficeTheme.bodyForegroundColor : COLORS.OUTLOOK_BLACK_FONT,
				currentOfficeTheme,
			)}
			slotProps={dataGridsx(currentOfficeTheme)}
			{...otherProps}
		/>
	);
};

export default React.memo(CustomDatagrid);
